/**
 * Renders one Evidence sub-agent answer. The caller has already replaced
 * resolved `<cite>` tags with `<cite-badge data-idx="N">` markers plus a
 * `data-idx → SimpleCitation[]` map; this is a thin Streamdown renderer over
 * that marked text. The reference list is built once by the timeline, not here.
 */

import type { EvidenceAgentImage } from '@heidi/agents/contracts';
import {
  normalizeHeadingDepth,
  responseProseClass,
  streamdownPlugins,
  streamdownRehypePlugins,
  useProseDensity,
} from '@heidi/answer-kit';
import type { SimpleCitation } from '@heidi/evidence/contracts';
import {
  cn,
  collapseCustomBlockBlankLines,
  normalizeKeySafetyNotesTags,
} from '@heidi/utils';
import { useMemo, useRef, type Context, type JSX } from 'react';
import { Streamdown } from 'streamdown';

import { getWorkHostPort } from '../../../work-home/work-home-ports';

export interface EvidenceAgentAnswerProps {
  markedText: string;
  /** `data-idx` → single-element `SimpleCitation[]` for the `cite-badge` handler. */
  citationMap: Map<number, SimpleCitation[]>;
  /** `image_id` → image for the `img-figure` handler; absent when inline images are off. */
  imageMap?: Map<string, EvidenceAgentImage>;
  streaming?: boolean;
  /** `null` in Projects — no scribe session backs this call; forwarded for analytics. */
  sessionId?: string | null;
  /** Evidence run this answer belongs to, so a Work citations row can find its badges. */
  runId?: string;
  /** Bumps when partner branding resolves late; forces a citation remount. */
  partnerConfigVersion?: number;
  className?: string;
}

export const EvidenceAgentAnswer = ({
  markedText,
  citationMap,
  imageMap,
  streaming = false,
  sessionId = null,
  runId,
  partnerConfigVersion,
  className,
}: EvidenceAgentAnswerProps): JSX.Element => {
  // Read the latest map via a ref so the cite-badge handler identity stays stable.
  const citationMapRef = useRef(citationMap);
  citationMapRef.current = citationMap;
  const imageMapRef = useRef(imageMap);
  imageMapRef.current = imageMap;

  // CommonMark closes an HTML block on the first blank line, so a blank line inside a
  // custom callout tag would split it and drop its tail back into plain prose — collapse
  // those inner gaps before Streamdown parses.
  const processedText = useMemo(() => {
    let result = markedText;
    result = collapseCustomBlockBlankLines(result, 'keypoints');
    result = collapseCustomBlockBlankLines(result, 'safetycallout');
    result = normalizeKeySafetyNotesTags(result);
    result = collapseCustomBlockBlankLines(result, 'keysafetynotes');
    // Citations are inline `cite-badge` markers here, not offsets, so the
    // heading rewrite needs no index map.
    return normalizeHeadingDepth(result);
  }, [markedText]);

  // A sub-agent answer renders inside a Work transcript, so it takes the
  // column's density from the host rather than reading `sessionId` or assuming
  // compact. Session state used to leave this answer at 14px body inside 16px
  // Work prose.
  const density = useProseDensity();

  const components = getWorkHostPort('bridge').useEvidenceAnswerComponents({
    citationMapRef,
    imageMapRef,
    streaming,
    density,
    sessionId,
    runId,
    partnerConfigVersion,
  }) as Parameters<typeof Streamdown>[0]['components'];

  const contextValue = useMemo(
    () => ({
      sessionId: sessionId ?? undefined,
      defaultSurface: 'inline_markdown_link' as const,
    }),
    [sessionId]
  );

  const ChatMessageContext = getWorkHostPort('evidence').ChatMessageContext as Context<
    typeof contextValue
  >;

  return (
    <ChatMessageContext.Provider value={contextValue}>
      <div className={cn('w-full min-w-0', className)}>
        <Streamdown
          components={components}
          className={responseProseClass(density)}
          isAnimating={streaming}
          mode={streaming ? undefined : 'static'}
          controls={{ table: true, code: true }}
          codeBlockMaxHeight={0}
          plugins={streamdownPlugins}
          rehypePlugins={streamdownRehypePlugins}
        >
          {processedText}
        </Streamdown>
      </div>
    </ChatMessageContext.Provider>
  );
};
